const ATTRIBUTION_VERSION = 1;
const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const ATTRIBUTION_STORAGE_KEY = 'capy_attribution_v1';
const SESSION_TOUCH_STORAGE_KEY = 'capy_attribution_session_v1';
const SESSION_SYNC_STORAGE_KEY = 'capy_attribution_cart_sync_v1';

const ATTRIBUTION_FIELD_KEYS = [
	'CCC source',
	'CCC medium',
	'CCC campaign',
	'CCC content',
	'CCC term',
	'CCC landing',
	'CCC referrer',
	'CCC session source',
	'CCC first source',
	'CCC signal',
	'CCC attribution model',
	'CCC captured at',
];

const CLICK_SOURCES = [
	{ parameter: 'twclid', source: 'x', medium: 'paid_social' },
	{ parameter: 'fbclid', source: 'meta', medium: 'paid_social' },
	{ parameter: 'gclid', source: 'google', medium: 'cpc' },
	{ parameter: 'gbraid', source: 'google', medium: 'cpc' },
	{ parameter: 'wbraid', source: 'google', medium: 'cpc' },
];

function cleanToken(value, maximumLength = 100) {
	return String(value ?? '')
		.trim()
		.replace(/[^a-zA-Z0-9._~+\-/]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, maximumLength);
}

function cleanLanding(pathname) {
	const cleaned = String(pathname ?? '/')
		.replace(/[\u0000-\u001f\u007f]/g, '')
		.slice(0, 150);
	return cleaned.startsWith('/') ? cleaned : '/';
}

function externalReferrerHost(referrer, storefrontHost) {
	if (!referrer) return '';
	try {
		const hostname = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
		const normalizedStorefrontHost = storefrontHost.toLowerCase().replace(/^www\./, '');
		return hostname === normalizedStorefrontHost ? '' : cleanToken(hostname);
	} catch {
		return '';
	}
}

/**
 * Build a privacy-minimized attribution touch. The full referrer and raw ad click
 * identifiers are deliberately excluded; the order only needs the channel signal.
 */
export function buildAttributionTouch(href, referrer = '', now = Date.now()) {
	const url = new URL(href);
	const parameters = url.searchParams;
	const referrerHost = externalReferrerHost(referrer, url.hostname);
	const clickSource = CLICK_SOURCES.find(({ parameter }) => parameters.has(parameter));

	const utmSource = cleanToken(parameters.get('utm_source'));
	const utmMedium = cleanToken(parameters.get('utm_medium'));
	const utmCampaign = cleanToken(parameters.get('utm_campaign'));
	const utmContent = cleanToken(parameters.get('utm_content'));
	const utmTerm = cleanToken(parameters.get('utm_term'));

	const source = utmSource || clickSource?.source || referrerHost || 'direct';
	const medium =
		utmMedium || clickSource?.medium || (referrerHost ? 'referral' : source === 'direct' ? 'direct' : 'unknown');
	const nonDirect = Boolean(
		utmSource || utmMedium || utmCampaign || utmContent || utmTerm || clickSource || referrerHost,
	);

	return {
		source,
		medium,
		campaign: utmCampaign,
		content: utmContent,
		term: utmTerm,
		landing: cleanLanding(url.pathname),
		referrer: referrerHost,
		signal: clickSource?.parameter ?? '',
		nonDirect,
		capturedAt: new Date(now).toISOString(),
	};
}

function isUsableRecord(record, now) {
	return Boolean(
		record &&
			record.version === ATTRIBUTION_VERSION &&
			record.first?.source &&
			Number.isFinite(record.expiresAt) &&
			record.expiresAt > now,
	);
}

/** Keep a 30-day first touch and standard last-non-direct touch. */
export function updateAttributionRecord(record, currentTouch, now = Date.now()) {
	const existing = isUsableRecord(record, now) ? record : null;
	return {
		version: ATTRIBUTION_VERSION,
		first: existing?.first ?? currentTouch,
		lastNonDirect: currentTouch.nonDirect ? currentTouch : (existing?.lastNonDirect ?? null),
		expiresAt:
			!existing || currentTouch.nonDirect ? now + ATTRIBUTION_MAX_AGE_MS : existing.expiresAt,
	};
}

/**
 * Values copied to Shopify cart attributes and hidden line-item properties.
 * These are intentionally human-readable in the order admin.
 */
export function attributionOrderFields(record, sessionTouch) {
	const selectedTouch = record.lastNonDirect ?? sessionTouch;
	return {
		'CCC source': selectedTouch.source,
		'CCC medium': selectedTouch.medium,
		'CCC campaign': selectedTouch.campaign,
		'CCC content': selectedTouch.content,
		'CCC term': selectedTouch.term,
		'CCC landing': sessionTouch.landing,
		'CCC referrer': sessionTouch.referrer || selectedTouch.referrer,
		'CCC session source': sessionTouch.source,
		'CCC first source': record.first.source,
		'CCC signal': selectedTouch.signal,
		'CCC attribution model': record.lastNonDirect ? 'last_non_direct_30d' : 'session',
		'CCC captured at': selectedTouch.capturedAt,
	};
}

function safeStorage(storage, operation, key, value) {
	try {
		if (operation === 'get') return storage.getItem(key);
		if (operation === 'remove') return storage.removeItem(key);
		storage.setItem(key, value);
	} catch {
		// Storage can be unavailable in strict/private browsing modes. The current
		// page can still inject attribution into a product form in that case.
	}
	return null;
}

function parseStoredJson(value) {
	if (!value) return null;
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
}

/** Double-underscore cart attributes stay hidden at checkout but remain visible in order admin. */
export function privateCartAttributes(fields) {
	return Object.fromEntries(Object.entries(fields).map(([key, value]) => [`__${key}`, value]));
}

export function clearedPrivateCartAttributes() {
	return Object.fromEntries(ATTRIBUTION_FIELD_KEYS.map((key) => [`__${key}`, '']));
}

async function syncCartAttributes(fields) {
	const root = window.Shopify?.routes?.root ?? '/';
	const response = await fetch(`${root}cart/update.js`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		credentials: 'same-origin',
		keepalive: true,
		body: JSON.stringify({ attributes: privateCartAttributes(fields) }),
	});
	if (!response.ok) throw new Error(`Attribution cart update failed (${response.status})`);
}

async function clearCartAttributes() {
	const root = window.Shopify?.routes?.root ?? '/';
	const response = await fetch(`${root}cart/update.js`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		credentials: 'same-origin',
		keepalive: true,
		body: JSON.stringify({ attributes: clearedPrivateCartAttributes() }),
	});
	if (!response.ok) throw new Error(`Attribution cart cleanup failed (${response.status})`);
}

let activeCartUpdateHandler = null;

function startAttribution() {
	if (window.__capyAttributionStarted) return;
	window.__capyAttributionStarted = true;

	const now = Date.now();
	const currentTouch = buildAttributionTouch(window.location.href, document.referrer, now);
	const storedSessionTouch = parseStoredJson(
		safeStorage(window.sessionStorage, 'get', SESSION_TOUCH_STORAGE_KEY),
	);
	const sessionTouch = storedSessionTouch?.source ? storedSessionTouch : currentTouch;
	if (!storedSessionTouch?.source) {
		safeStorage(window.sessionStorage, 'set', SESSION_TOUCH_STORAGE_KEY, JSON.stringify(sessionTouch));
	}

	const storedRecord = parseStoredJson(safeStorage(window.localStorage, 'get', ATTRIBUTION_STORAGE_KEY));
	const record = updateAttributionRecord(storedRecord, currentTouch, now);
	safeStorage(window.localStorage, 'set', ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));

	const fields = attributionOrderFields(record, sessionTouch);

	const signature = JSON.stringify(fields);
	const syncOnce = () => {
		if (safeStorage(window.sessionStorage, 'get', SESSION_SYNC_STORAGE_KEY) === signature) return;
		syncCartAttributes(fields)
			.then(() => safeStorage(window.sessionStorage, 'set', SESSION_SYNC_STORAGE_KEY, signature))
			.catch(() => {});
	};

	syncOnce();
	activeCartUpdateHandler = (event) => {
		Promise.resolve(event.promise)
			.then(() => syncCartAttributes(fields))
			.catch(() => {});
	};
	document.addEventListener('shopify:cart:lines-update', activeCartUpdateHandler);
}

function stopAttribution() {
	const hadAttribution = Boolean(
		window.__capyAttributionStarted ||
			safeStorage(window.localStorage, 'get', ATTRIBUTION_STORAGE_KEY) ||
			safeStorage(window.sessionStorage, 'get', SESSION_TOUCH_STORAGE_KEY) ||
			safeStorage(window.sessionStorage, 'get', SESSION_SYNC_STORAGE_KEY),
	);

	if (activeCartUpdateHandler) {
		document.removeEventListener('shopify:cart:lines-update', activeCartUpdateHandler);
		activeCartUpdateHandler = null;
	}
	window.__capyAttributionStarted = false;
	safeStorage(window.localStorage, 'remove', ATTRIBUTION_STORAGE_KEY);
	safeStorage(window.sessionStorage, 'remove', SESSION_TOUCH_STORAGE_KEY);
	safeStorage(window.sessionStorage, 'remove', SESSION_SYNC_STORAGE_KEY);
	if (hadAttribution) clearCartAttributes().catch(() => {});
}

export function trackingProcessingAllowed(privacy) {
	if (!privacy) return false;
	if (
		typeof privacy.analyticsProcessingAllowed === 'function' &&
		typeof privacy.marketingAllowed === 'function'
	) {
		return privacy.analyticsProcessingAllowed() && privacy.marketingAllowed();
	}
	return typeof privacy.userCanBeTracked === 'function' && privacy.userCanBeTracked();
}

function startWhenTrackingIsAllowed() {
	document.addEventListener('trackingConsentAccepted', startAttribution, { once: true });
	document.addEventListener('visitorConsentCollected', (event) => {
		if (event.detail?.analyticsAllowed && event.detail?.marketingAllowed) {
			startAttribution();
		} else {
			stopAttribution();
		}
	});

	const beginAfterPrivacyCheck = () => {
		const privacy = window.Shopify?.customerPrivacy;
		if (trackingProcessingAllowed(privacy)) startAttribution();
		else stopAttribution();
	};

	if (window.Shopify?.loadFeatures) {
		window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], beginAfterPrivacyCheck);
	} else {
		beginAfterPrivacyCheck();
	}
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
	startWhenTrackingIsAllowed();
}
